import React from "react";
import { Box, Typography, ListItem, ListItemIcon } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { ReactComponent as CheckMarkCircle } from "../../../icons/CheckMarkCircle.svg";
import { useCurrentBrand } from "../../../hooks/useCurrentWorkspace";
import { IphoneIconPreviewCanvas } from "../IphoneIconPreviewCanvas";
import { IphoneSplashPreviewCanvas } from "../IphoneSplashPreviewCanvas";
import { SharablePreviewCanvas } from "./SharablePreviewCanvas";
import demoWorkoutSharing from "../../../assets/workout-sharing-demo.png";

const useStyles = makeStyles((theme) => ({
  header: {
    marginBottom: theme.spacing(2.5),
  },
  titleBox: {
    display: "flex",
    alignItems: "center",
    gap: 0,
    flexDirection: "column",
    marginBottom: theme.spacing(2),

    [theme.breakpoints.up("sm")]: {
      flexDirection: "row",
      gap: theme.spacing(2),
      marginBottom: 0,
    },
  },
  title: {
    fontSize: 32,
    fontWeight: "bold",
    color: theme.palette.common.black,
    marginBottom: theme.spacing(1),
  },

  h2: {
    fontSize: 28,
    fontWeight: "bold",
    color: theme.palette.common.black,
    marginBottom: theme.spacing(1),
  },
  statusBox: {
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    padding: theme.spacing(0.5, 2),
    flexShrink: 0,
    marginBottom: 4,
  },
  status: {
    fontSize: 12,
    fontWeight: 600,
    textTransform: "uppercase",
  },
  text: {
    fontSize: 16,
    fontWeight: 500,
    color: theme.palette.common.black,
  },
  features: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "center",
    marginBottom: theme.spacing(4.5),
    marginTop: theme.spacing(3),
  },
  feature: {
    "&:not(:last-child)": {
      marginBottom: theme.spacing(3.5),
    },

    [theme.breakpoints.up("md")]: {
      "&:not(:last-child)": {
        marginBottom: theme.spacing(3.5),
      },
    },
  },
  check: {
    marginRight: theme.spacing(1.25),

    "& svg": {
      width: 30,
      height: 30,
      color: theme.palette.primary.main,
    },
  },
  uploadPreview: {
    width: "100%",
    display: "flex",
    justifyContent: "start",
    flexDirection: "column",
    gap: theme.spacing(2),
    alignItems: "start",
    marginBottom: theme.spacing(4),

    [theme.breakpoints.up("md")]: {
      flexDirection: "row",
    },
  },
  sharablePreview: {
    maxWidth: theme.spacing(42),
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 600,
    color: theme.palette.common.black,
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
}));

interface AppInProgressContentProps {}

export function AppInProgressContent() {
  const s = useStyles();
  const {
    appName,
    isBrandedAppPublished,
    brandedAppLogo,
    brandedAppSplashScreen,
    brandedAppSplashBackgroundColor,
    brandedAppBanner,
  } = useCurrentBrand();

  const features = [
    {
      text: `Custom ${appName} app loading screen`,
    },
    {
      text: `Your logo and branding inside ${appName} app`,
    },
    {
      text: `No mentioning of Stridist inside ${appName} client app`,
    },
    {
      text: "Branded email and push notifications",
    },
  ];

  return (
    <Box className={s.header}>
      <Box className={s.titleBox}>
        <Typography className={s.title}>{appName} Branded App</Typography>
        <Box className={s.statusBox}>
          <Typography className={s.status}>in development</Typography>
        </Box>
      </Box>
      <Typography className={s.text}>
        Your custom branded app is under the development by Stridist dev team.
      </Typography>
      <Typography
        className={s.text}
        sx={{ marginTop: isBrandedAppPublished ? 2 : 0 }}
      >
        Very soon it will be available for clients on both Apple and Google
        stores. Which also includes:
      </Typography>
      <>
        <Box className={s.features}>
          {features.map(({ text }, index) => (
            <ListItem key={index} className={s.feature}>
              <ListItemIcon
                className={s.check}
                children={<CheckMarkCircle />}
              />
              <Typography children={text} className={s.text} />
            </ListItem>
          ))}
        </Box>
      </>

      <Typography className={s.sectionTitle}>Your Branding</Typography>
      <ListItem className={s.feature}>
        <ListItemIcon className={s.check} children={<CheckMarkCircle />} />
        <Typography className={s.text}>App icon and splash screen</Typography>
      </ListItem>
      <Box className={s.uploadPreview}>
        <Box className={s.sharablePreview}>
          <IphoneIconPreviewCanvas
            iconUrl={brandedAppLogo}
            appName={appName}
            previewTitle="Branded App Preview"
          />
        </Box>
        <IphoneSplashPreviewCanvas
          splashUrl={brandedAppSplashScreen}
          backgroundColor={brandedAppSplashBackgroundColor}
        />
      </Box>

      <ListItem className={s.feature}>
        <ListItemIcon className={s.check} children={<CheckMarkCircle />} />
        <Typography className={s.text}>
          Internal app branding elements
        </Typography>
      </ListItem>
      <Box className={s.uploadPreview}>
        <SharablePreviewCanvas
          brandedAppBanner={brandedAppBanner}
          backgroundUrl={demoWorkoutSharing}
          title="Workout Sharing Preview"
        />
        <SharablePreviewCanvas brandedAppBanner={brandedAppBanner} />
      </Box>
    </Box>
  );
}
